import { t } from 'i18next';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { OrganizationSubscriptionStatus } from '__generated__/graphql';

export enum OrganizationSubStatus {
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
}

export function isFreeAccount(subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'productHandle'>): boolean {
  return subscriptionStatus.productHandle?.includes('free') ?? true;
}

export function isProAccount(subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'productHandle'>): boolean {
  if (isNil(subscriptionStatus.productHandle)) {
    return false;
  }
  return subscriptionStatus.productHandle.includes('pro') || subscriptionStatus.productHandle.includes('enterprise');
}

export function isFreeTrialAccount(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'status' | 'productHandle' | 'trialEndedAt'>
): boolean {
  if (isProAccount(subscriptionStatus)) {
    if (
      subscriptionStatus.status === OrganizationSubStatus.TRIALING &&
      !isNil(subscriptionStatus.trialEndedAt) &&
      DateTime.fromISO(subscriptionStatus.trialEndedAt).toSeconds() > DateTime.local().toSeconds()
    ) {
      return true;
    }
  }
  return false;
}

export function hasCard(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'cardType' | 'cardLastFour'>
): boolean {
  return !isNil(subscriptionStatus.cardType) && !isNil(subscriptionStatus.cardLastFour);
}

export function isFreeTrialUpgraded(
  subscriptionStatus: Pick<
    OrganizationSubscriptionStatus,
    'cardType' | 'cardLastFour' | 'status' | 'productHandle' | 'trialEndedAt'
  >
): boolean {
  return isFreeAccount(subscriptionStatus) && hasCard(subscriptionStatus);
}

export function isTrialEnded(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'hasTrialed' | 'status' | 'productHandle' | 'trialEndedAt'>
): boolean {
  return !isNil(subscriptionStatus.hasTrialed) && subscriptionStatus.hasTrialed && isFreeAccount(subscriptionStatus);
}

export function isTrialAvailable(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'hasTrialed' | 'status' | 'productHandle' | 'trialEndedAt'>
): boolean {
  return !isNil(subscriptionStatus.hasTrialed) && !subscriptionStatus.hasTrialed && isFreeAccount(subscriptionStatus);
}

export function isPastDue(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'status' | 'productHandle' | 'trialEndedAt'>
): boolean {
  return subscriptionStatus.status === OrganizationSubStatus.PAST_DUE && !isFreeTrialAccount(subscriptionStatus);
}

export function isSubscriptionLockedOut(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'status' | 'productHandle' | 'trialEndedAt'>
): boolean {
  return subscriptionStatus.status === OrganizationSubStatus.CANCELED && !isFreeTrialAccount(subscriptionStatus);
}

export function subscriptionPlanString(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'status' | 'productHandle' | 'trialEndedAt'>
): string {
  if (isProAccount(subscriptionStatus)) {
    if (isFreeTrialAccount(subscriptionStatus)) {
      return t('Pro Free Trial');
    }
    return t('Pro');
  }
  return t('Free');
}

export function isSubscriptionAnnual(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'annualSubscriptionCount'>
): boolean {
  return !isNil(subscriptionStatus.annualSubscriptionCount); // if annualSubscriptionCount has a value, the organization is using an annual plan
}

export function isSubscriptionMonthly(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'annualSubscriptionCount'>
): boolean {
  return !isSubscriptionAnnual(subscriptionStatus);
}

export function isInvoice(
  subscriptionStatus: Pick<OrganizationSubscriptionStatus, 'annualSubscriptionPendingInvoiceId'>
): boolean {
  return !isNil(subscriptionStatus.annualSubscriptionPendingInvoiceId);
}

export function firstPaymentDate(
  subscriptionStatus: Pick<
    OrganizationSubscriptionStatus,
    | 'annualSubscriptionCount'
    | 'annualSubscriptionPendingInvoiceId'
    | 'trialEndedAt'
    | 'annualSubscriptionDate'
    | 'nextAssessmentAt'
    | 'status'
    | 'productHandle'
  >
): DateTime | undefined {
  if (isSubscriptionAnnual(subscriptionStatus)) {
    if (isInvoice(subscriptionStatus) && !isNil(subscriptionStatus.trialEndedAt)) {
      return DateTime.fromISO(subscriptionStatus.trialEndedAt).plus({ months: 1 });
    } else if (!isNil(subscriptionStatus.annualSubscriptionDate)) {
      return DateTime.fromISO(subscriptionStatus.annualSubscriptionDate);
    }
  } else if (isFreeTrialAccount(subscriptionStatus) && !isNil(subscriptionStatus.trialEndedAt)) {
    return DateTime.fromISO(subscriptionStatus.trialEndedAt).plus({ months: 1 });
  }

  if (subscriptionStatus.nextAssessmentAt) {
    return DateTime.fromISO(subscriptionStatus.nextAssessmentAt);
  }

  return undefined;
}

export function nextPaymentDate(
  subscriptionStatus: Pick<
    OrganizationSubscriptionStatus,
    | 'annualSubscriptionCount'
    | 'annualSubscriptionPendingInvoiceId'
    | 'trialEndedAt'
    | 'annualSubscriptionDate'
    | 'nextAssessmentAt'
    | 'status'
    | 'productHandle'
  >
): DateTime | undefined {
  if (isSubscriptionMonthly(subscriptionStatus)) {
    return firstPaymentDate(subscriptionStatus);
  }
  return firstPaymentDate(subscriptionStatus)?.plus({ years: 1 });
}
